import * as angular from 'angular';

export type categoryTypes = 'Custom' | 'MedSurg';

export interface Category {
  categoryType: string;
  terminationDate: string;
  notes: string;
  unspscDetails: [
    {
      standardCategoryId: number;
      name: string;
      standardCategoryName: string;
      id: number;
      products: [
        {
          seller: {
            name: string;
            id: number;
          };
          catalogNumber: string;
          manufacturerName: string;
          description: string;
          id: number;
        }
      ];
    }
  ];
  unspscs: [
    {
      name: string;
      id: number;
    }
  ];
  id: number;
  effectiveDate: string;
  name?: string;
}

export default class CategoryService {
  baseUrl = '/aptitude/api/categories';

  // This is not really injecting this is coming from api.service
  constructor(private $http, private $q) {
    'ngInject';
  }

  getCategories(rfpEligibleCategories?: boolean): ng.IPromise<any> {
    return this.$http.get(this.baseUrl, {
      params: {
        rfpEligibleCategories
      }
    });
  }

  getStandardCategoriesWithSpend() {
    return this.$http.get(`${this.baseUrl}?categoryType=STANDARD&expand=spend`);
  }

  getAllCategories() {
    return this.$http.get(`${this.baseUrl}?expand=spend`);
  }

  getCategory(categoryId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${categoryId}`);
  }

  getProductsInCategory(categoryId, useSePath = false) {
    // Not sure if we will be completely retiring the /categories path with /categories-se
    // or if it will only be for this request.
    const baseUrl = useSePath ? '/aptitude/api/categories-se' : this.baseUrl;
    return this.$http.get(`${baseUrl}/${categoryId}/products`);
  }

  // Get search result products
  getSearchProducts(searchValue) {
    let canceller = this.$q.defer();
    let cancel = (reason) => {
      canceller.resolve(reason);
    };

    let promise = this.$http.get(
      `/aptitude/api/products/_search?aggregation=category&searchValue=${searchValue}&aggregateOnly=Y`,
      {
        timeout: canceller.promise
      }
    );

    return {
      promise,
      cancel
    };
  }

  // API services are grouped by base path - '/aptitude/api/products/' needs to go in a separate service.
  searchRequestForProductsHelper(args) {
    let canceller = this.$q.defer();
    let cancel = (reason) => {
      canceller.resolve(reason);
    };

    let promise = this.$http.post(
      `/aptitude/api/products/searchrequests`,
      args,
      { timeout: canceller.promise }
    );

    return {
      promise,
      cancel
    };
  }

  searchRequestForProductsInPPDetailsPageCrossesModal(
    searchValue,
    scopeResourceId
  ) {
    return this.searchRequestForProductsHelper({
      scopeResourceType: 'rfp',
      scopeResourceId: scopeResourceId,
      aggregateOnly: false,
      searchValue: searchValue
    });
  }

  searchRequestForProductsInCatalogProductsPageCrossesModal(
    searchValue,
    scopeResourceId
  ) {
    return this.searchRequestForProductsHelper({
      scopeResourceType: 'category',
      scopeResourceId: scopeResourceId,
      aggregateOnly: false,
      searchValue: searchValue
    });
  }

  searchRequestForProductsInCatalog(searchValue) {
    return this.searchRequestForProductsHelper({
      aggregateOnly: true,
      aggregationFields: ['category'],
      searchValue: searchValue
    });
  }

  renameCategory(categoryId: number, categoryName: string): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${categoryId}`, {
      categoryName
    });
  }

  /**
   * Add USPSCS to category
   * @param {number}: categoryId
   * @param [{standardCategoryId: number}]: standardCategoryIds
   */
  addUnspscsToCategory(
    categoryId: number,
    unspscIds?: any[]
  ): ng.IPromise<any> {
    return this.$http.post(`${this.baseUrl}/${categoryId}/unspscs`, unspscIds);
  }

  /**
   * Remove one unspsc from a "standard category" or by standardCategoryId from a custom category
   *
   * @param  {number} categoryId
   * @param  {number} unspscId
   * @return {ng.IPromise<any>}
   */
  removeUnspscFromCategory(
    categoryId: number,
    unspscId?: number
  ): ng.IPromise<any> {
    return this.$http.delete(
      `${this.baseUrl}/${categoryId}/unspscs/${unspscId}`
    );
  }

  /**
   * Remove all unspscs from a "standard category" or by standardCategoryId from a custom category
   *
   * @param  {number} categoryId
   * @param  {number} standardCategoryId (optional) For removing unspscs from a custom category
   * @return {ng.IPromise<any>}
   */
  removeUnspscsFromCategory(
    categoryId: number,
    standardCategoryId?: number
  ): ng.IPromise<any> {
    return this.$http.delete(`${this.baseUrl}/${categoryId}/unspscs`, {
      params: {
        standardCategoryId
      }
    });
  }
}
